/**
 * ───────────────────────────────────────────────────────────────────
 *  COLOR PALETTES
 *  Each theme is just a set of CSS custom properties. Add one by copying
 *  a block below and changing the values. Switch at runtime with `/theme`.
 *
 *  The first entry is the default. Editorial palettes come first; the
 *  original CRT phosphors are kept below them as period options.
 * ───────────────────────────────────────────────────────────────────
 */

export interface Theme {
  /** key used by the /theme command, lowercase, no spaces */
  id: string;
  label: string;
  vars: {
    bg: string; // page background
    surface: string; // terminal + pane panels
    surfaceBar: string; // title bars
    border: string; // hairlines
    text: string; // primary text
    muted: string; // secondary text, metadata
    accent: string; // the one signal color: prompt, links, headings
    accent2: string; // emphasis text
    ok: string;
    warn: string;
  };
}

export const themes: Theme[] = [
  /* ── Editorial ──────────────────────────────────────────────────
     Restrained, high-legibility palettes. One accent, used sparingly;
     hierarchy comes from scale and space rather than color. */
  {
    id: "ink",
    label: "Ink",
    vars: {
      bg: "#0c0c0d",
      surface: "#111113",
      surfaceBar: "#141417",
      border: "rgba(255, 255, 255, 0.09)",
      text: "#e9e7e2",
      muted: "#85827b",
      accent: "#d9a441",
      accent2: "#f2e3c8",
      ok: "#8fb08a",
      warn: "#d08770",
    },
  },
  {
    id: "paper",
    label: "Paper",
    vars: {
      bg: "#fbfaf7",
      surface: "#ffffff",
      surfaceBar: "#f3f1ec",
      border: "rgba(0, 0, 0, 0.11)",
      text: "#1a1917",
      muted: "#6b6862",
      accent: "#9c5d1c",
      accent2: "#3d3a34",
      ok: "#4a7a4e",
      warn: "#a6462b",
    },
  },
  {
    id: "slate",
    label: "Slate",
    vars: {
      bg: "#0d0f12",
      surface: "#121519",
      surfaceBar: "#161a1f",
      border: "rgba(255, 255, 255, 0.08)",
      text: "#e3e6ea",
      muted: "#7c848e",
      accent: "#7fa7c9",
      accent2: "#cfe0ee",
      ok: "#7fb8a3",
      warn: "#d79a76",
    },
  },

  /* ── CRT phosphors ──────────────────────────────────────────────
     The original look. Kept because they're fun, not because they're
     the house style — `/theme amber` brings the old site back. */
  {
    id: "amber",
    label: "Amber (P3)",
    vars: {
      bg: "#0f0a02",
      surface: "#140d03",
      surfaceBar: "#1e1305",
      border: "rgba(255, 176, 0, 0.22)",
      text: "#ffc24d",
      muted: "#9c6b18",
      accent: "#ffb000",
      accent2: "#ffd98a",
      ok: "#ffd27a",
      warn: "#ff5a1a",
    },
  },
  {
    id: "green",
    label: "Green (P1)",
    vars: {
      bg: "#03110a",
      surface: "#041209",
      surfaceBar: "#061a0c",
      border: "rgba(51, 255, 102, 0.20)",
      text: "#5bff8f",
      muted: "#2a8f4d",
      accent: "#33ff66",
      accent2: "#9affb8",
      ok: "#33ff66",
      warn: "#f0ff5e",
    },
  },
  {
    id: "ibm",
    label: "IBM 5151",
    vars: {
      bg: "#04100c",
      surface: "#051410",
      surfaceBar: "#071c16",
      border: "rgba(60, 255, 190, 0.20)",
      text: "#4dffce",
      muted: "#239b7c",
      accent: "#1ff0b0",
      accent2: "#8dffe0",
      ok: "#1ff0b0",
      warn: "#f0ff5e",
    },
  },
  {
    id: "c64",
    label: "Commodore",
    vars: {
      bg: "#1a1370",
      surface: "#30278a",
      surfaceBar: "#3a309e",
      border: "rgba(180, 170, 255, 0.32)",
      text: "#b3a8ff",
      muted: "#6d62c4",
      accent: "#8e84ff",
      accent2: "#d6d0ff",
      ok: "#9affb8",
      warn: "#ffd166",
    },
  },
  {
    id: "gameboy",
    label: "Game Boy",
    vars: {
      bg: "#0b260b",
      surface: "#0f380f",
      surfaceBar: "#164816",
      border: "rgba(155, 188, 15, 0.32)",
      text: "#9bbc0f",
      muted: "#306230",
      accent: "#8bac0f",
      accent2: "#c2d62f",
      ok: "#9bbc0f",
      warn: "#c2d62f",
    },
  },
  /* The SNES/Super Famicom palette: the console's two purples for the
     chrome, and its four face buttons doing the semantic work — the violet
     pair as accents, the green and red buttons as ok/warn. */
  {
    id: "snes",
    label: "Super Nintendo",
    vars: {
      bg: "#16121f",
      surface: "#1d1829",
      surfaceBar: "#251f35",
      border: "rgba(178, 160, 255, 0.20)",
      text: "#ded7f0",
      muted: "#8a7cb8",
      accent: "#a68cff",
      accent2: "#ddd2ff",
      ok: "#7bc86c",
      warn: "#e8604c",
    },
  },
  {
    id: "mono",
    label: "Mono (P4)",
    vars: {
      bg: "#080808",
      surface: "#101010",
      surfaceBar: "#181818",
      border: "rgba(255, 255, 255, 0.16)",
      text: "#e8e8e8",
      muted: "#7a7a7a",
      accent: "#f5f5f5",
      accent2: "#c4c4c4",
      ok: "#d6d6d6",
      warn: "#bdbdbd",
    },
  },
];

const STORAGE_KEY = "portfolio.theme";

/** The two palettes the top-right toggle switches between. Everything else
    in `themes` is reachable only through `/theme` — they're easter eggs. */
export const DARK_ID = "ink";
export const LIGHT_ID = "paper";

export function getTheme(id: string): Theme | undefined {
  return themes.find((t) => t.id === id);
}

/* ── Change notifications ─────────────────────────────────────
   So the toggle button can follow along when a theme is set from
   `/theme`, without the terminal needing to know the button exists. */

type ThemeListener = (theme: Theme) => void;
const listeners = new Set<ThemeListener>();

/** Subscribe to theme changes. Returns an unsubscribe function. */
export function onThemeChange(fn: ThemeListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Push a theme's variables onto :root so all CSS picks them up. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  const v = theme.vars;
  root.style.setProperty("--bg", v.bg);
  root.style.setProperty("--surface", v.surface);
  root.style.setProperty("--surface-bar", v.surfaceBar);
  root.style.setProperty("--border", v.border);
  root.style.setProperty("--text", v.text);
  root.style.setProperty("--muted", v.muted);
  root.style.setProperty("--accent", v.accent);
  root.style.setProperty("--accent-2", v.accent2);
  root.style.setProperty("--ok", v.ok);
  root.style.setProperty("--warn", v.warn);
  root.dataset.theme = theme.id;

  // Let the browser render form controls and scrollbars to match.
  root.style.colorScheme = isLightTheme(theme) ? "light" : "dark";

  for (const fn of listeners) fn(theme);
}

/** Is this a light palette? Rough luminance check on the background, so it
    works for any theme someone adds without extra bookkeeping. */
export function isLightTheme(theme: Theme): boolean {
  const m = /^#([0-9a-f]{6})$/i.exec(theme.vars.bg.trim());
  if (!m) return false;
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
}

/** The palette to use when the visitor has no saved preference: follow the
    operating system rather than imposing one. */
export function preferredTheme(): Theme {
  const wantsLight = matchMedia("(prefers-color-scheme: light)").matches;
  return getTheme(wantsLight ? LIGHT_ID : DARK_ID) ?? themes[0]!;
}

export function saveTheme(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* storage may be unavailable; ignore */
  }
}

export function loadSavedThemeId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
